import {doc} from '../docs.js';

const RUBIK_SIZE = 27;
doc(
    'rubik.init',
    {
    },
    "Initializes a rubiks cube returning a list of indices"
);

export const init = () => {
    return ([]) => {
        let list = [];
        for (let i=0; i < RUBIK_SIZE; i++) {
            list.push(i);
        }
        return [list];
    };
};

    
/**
 * A row of a cube starts w following indices
 *
 *   2 5 8
 *   1 4 7 
 *   0 3 6
 *
 * Turning LEFT gives you 
 * 
 *   0 1 2
 *   3 4 5
 *   6 7 8
 *
 * Turning RIGHT gives you
 *
 *   8 7 6
 *   5 4 2
 *   2 1 0
 *
**/

/**
   LEFT=0
   RIGHT=1
   *
   * Input is a list of indices representing the FACE
   * being turned (either left or right). 
   * When you turn the cube's face, ONLY the elements in the face will be transformed.
   * So if we simply rearrange these indices, and then replace that section
   * of the ENTIRE list of indices, we shouldbe good.
 */

const LEFT_INDICES  = [6,3,0,7,4,1,8,5,2];
const RIGHT_INDICES = [2,5,8,1,4,7,0,3,6];

doc(
    'rubik.turnFace',
    {
        face: "List of indices representing the face being turned",
        direction: "0 for left and 1 for right",
        dimension: "x or y"
    },
    "Turn a face (represented as list of indices) either left (0) or right (1)",
    () => 1,
    {
        inlets: 3
    });
export const turnFace = (object, direction, dim) => {
    return ([face]) => {
        let _direction = dim() === "y" ? !direction() : direction();
        let indices = _direction ? LEFT_INDICES : RIGHT_INDICES;
        let transformedFace = [];
        let i=0;
        for (let index of indices) {
            transformedFace[i] = face[index];
            i++;
        }
        return [transformedFace];
    };
};

/**
 * 
 * A rubiks cube's configuration is represented as a list of indices
 * With the squares (going from bottom to top) being:
 *   Bottom: 0-8
 *   Middle: 9-15
 *   Top:    16-27
 *
 * We will want to "access" specific faces of the rubix cube.
 * We need to specifiy which "dimension" we are working in x,y,z
 * For example:
 *   If it's z and we want z=0, we would get 0-8
 *   If it's x and we want x=0, we would get 0,1,2, 9,10,11, 16,17,18
 *
 * So we need an access function that takes the dimension and value in that dimension
 */

doc(
    "rubik.getFace",
    {
        indices: "list of indices of the rubik cube (in its current configuration)",
        value: "value in dimension we want to acces",
        dimension: "x,y,z"
    },
    "Gets a list of indices representing a desired face",
    () => 1,
    {
        inlets: 3
    });

let Y_INDICES = [[0,1,2,3,4,5,6,7,8], [9,10,11,12,13,14,15,16,17], [18,19,20,21,22,23,24,25,26]];
let X_INDICES = [[0,1,2,9,10,11,18,19,20], [3,4,5,12,13,14,21,22,23], [6,7,8,15,16,17,24,25,26]];
export const getFace = (object, value, dimension) => {
    return ([indices]) => {
        let _value = value();
        let _dimension = dimension();

        let dimIndices = _dimension === 'y' ? Y_INDICES[_value] : X_INDICES[_value];
        let face = [];
        for (let i=0; i < dimIndices.length; i++) {
            face.push(indices[dimIndices[i]]);
        }
        return [face];
    };
};

/**
 * When we make a move, we first do "getFace" to get the indices for that face
 * then do "turnFace" to transform those indices into the new set of indices
 * 
 * At this point, we need to replace the face in the list of indices representing the entire
 * rubik's cube, with these transformed indices
 */

doc(
    'rubik.setFace',
    {
        indices: "list of indices of the rubik cube (in its current configuration)",
        value: "value in dimension we want to acces",
        dimension: "x,y,z"
    },
    "Sets a list of indices representing a desired face in the rubiks indices and return the new cube",
    () => 1,
    {
        inlets: 4
    });

export const setFace = (object, faceIndices, value, dimension) => {
    return ([indices]) => {
        let _value = value();
        let _dimension = dimension();
        let _faceIndices = faceIndices();
        let original = [... indices];
        indices = [... indices];

        let dimIndices = _dimension === 'y' ? Y_INDICES[_value] : X_INDICES[_value];
        for (let i=0; i < dimIndices.length; i++) {
            indices[dimIndices[i]] = _faceIndices[i];
        }
        return [indices];
    };
};

doc(
    'rubik.applyIndices',
    {
        "list to shuffle": "",
        "indices": ""
    },
    "Transforms a given a list using the indices passed in the right inlet",
    () => 1,
    {
        inlets: 2
    });
export const applyIndices= (object, indices) => {
    return ([transformations]) => {
        let _indices = indices();
        if (!_indices) {
            return [transformations];
        }
        let newList = [];
        for (let i=0; i < transformations.length; i++) {
            let transformation = transformations[i];
            // We have a transformation for rubik position i
            // However, due to rubiks cube moves, rubiks position i (at current time) may
            // have a different SDF entirely
            // The index tells us that indices[i] is the SDF that is at position i
            newList[_indices[i]] = transformation;
        }
        return [newList];
    };
};

export const rubix = {
    'rubik.turnFace': turnFace,
    'rubik.init': init,
    'rubik.getFace': getFace,
    'rubik.setFace': setFace,
    'rubik.applyIndices': applyIndices
};
